/**
 * Pure helpers for assembling the live plain-transcript feed (timestamped
 * turns, no speaker attribution — see MDRS diarization-removal work) shown
 * while a Scribe session is recording.
 *
 * Kept separate from index.tsx so the timestamp math is unit-testable
 * without an AudioContext / Azure connection. This is a UI-only viewing aid:
 * the backend's batch/offline diarization pipeline remains the sole writer
 * of the persisted, speaker-labelled `segmented_full_transcript`.
 */

export type LiveTranscriptTurn = {
  /** [startSec, endSec] relative to session start. */
  timestamp: [number, number];
  timestamp_hhmmss: [string, string];
  text: string;
};

/** Format a number of seconds as "HH:MM:SS". */
export function formatHhmmss(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const hh = Math.floor(s / 3600);
  const mm = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}`;
}

/**
 * Append a finalized turn to the running plain-transcript turn list.
 *
 * Returns a NEW array (never mutates the input) so it can drive React state.
 * Empty/whitespace-only turns are ignored and the list is returned as-is.
 */
export function appendLiveTurn(
  turns: LiveTranscriptTurn[],
  turn: { text: string; startSec: number; endSec: number }
): LiveTranscriptTurn[] {
  const text = turn.text.trim();
  if (!text) return turns;

  const start = Math.max(0, turn.startSec);
  const end = Math.max(start, turn.endSec);
  return [
    ...turns,
    {
      timestamp: [start, end],
      timestamp_hhmmss: [formatHhmmss(start), formatHhmmss(end)],
      text,
    },
  ];
}
